import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  useAddSignup,
  useUpdateSignup,
  useViewing,
} from "@/hooks/api/useViewings";

const STATUS_TONE: Record<string, "neutral" | "success" | "danger" | "brand"> = {
  invited: "neutral",
  confirmed: "success",
  declined: "danger",
  attended: "brand",
  no_show: "danger",
};

const NEXT_STATUS = ["invited", "confirmed", "attended", "no_show", "declined"];

export function ViewingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: slot, isLoading, isError } = useViewing(id);
  const addSignup = useAddSignup(id ?? "");
  const updateSignup = useUpdateSignup(id ?? "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !slot)
    return <p className="text-sm text-danger">Viewing not found.</p>;

  return (
    <div className="space-y-6">
      <Link
        to="/viewings"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Viewings
      </Link>

      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          {new Date(slot.scheduled_at).toLocaleString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {slot.duration_minutes} minutes · {slot.signups.filter((s) =>
            ["invited", "confirmed"].includes(s.status),
          ).length}{" "}
          / {slot.max_capacity} booked
          {slot.is_cancelled ? " · cancelled" : ""}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Attendees</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name) return;
              addSignup.mutate(
                { name, email: email || null, status: "invited" },
                {
                  onSuccess: () => {
                    setName("");
                    setEmail("");
                  },
                  onError: () => toast.error("Could not add attendee"),
                },
              );
            }}
          >
            <Input
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10 w-40"
            />
            <Input
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 w-52"
            />
            <Button type="submit" size="sm" variant="outline">
              Add attendee
            </Button>
          </form>

          {slot.signups.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {slot.signups.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 border-b border-border/60 pb-2 last:border-0"
                >
                  <span>
                    {s.name ?? "—"}
                    {s.email ? (
                      <span className="text-muted-foreground"> · {s.email}</span>
                    ) : null}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge tone={STATUS_TONE[s.status] ?? "neutral"}>
                      {s.status.replace(/_/g, " ")}
                    </Badge>
                    <select
                      value={s.status}
                      onChange={(e) =>
                        updateSignup.mutate({
                          signupId: s.id,
                          status: e.target.value,
                        })
                      }
                      className="h-8 rounded-md border border-input bg-card px-2 text-xs capitalize"
                    >
                      {NEXT_STATUS.map((st) => (
                        <option key={st} value={st}>
                          {st.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No attendees yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
